import React, { useEffect, useCallback } from 'react';
import { Drawer, Form, Button, Space, Spin, message } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import BenefitForm from './BenefitForm';
import {
  getBenefitDetail,
  createBenefit,
  updateBenefit,
} from '../api';
import { BenefitFormData, BenefitRecord, BenefitType } from '../types';
import { QUERY_KEYS, DRAWER_CONFIG, FORM_DEFAULT_VALUES } from '../constants';
import {
  convertTypeLimitForForm,
  convertTypeLimitForSubmit,
} from '../utils/typeLimitUtils';

interface BenefitDrawerProps {
  open: boolean;
  editingBenefit?: BenefitRecord | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const BenefitDrawer: React.FC<BenefitDrawerProps> = ({
  open,
  editingBenefit,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<BenefitFormData>();
  const isEdit = !!editingBenefit?.id;
  const benefitId = editingBenefit?.id;

  // 获取详情
  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: [QUERY_KEYS.BENEFIT_DETAIL, benefitId],
    queryFn: () => getBenefitDetail(benefitId!),
    enabled: isEdit && !!benefitId,
  });

  // 监听 type 变化，在编辑模式下切换类型时处理 typeLimit
  const currentType = Form.useWatch('type', form);

  // 创建
  const createMutation = useMutation({
    mutationFn: createBenefit,
    onSuccess: () => {
      message.success('创建成功');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BENEFIT_LIST] });
      handleClose();
      onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('创建失败:', error);
      message.error(error.message || '创建失败');
    },
  });

  // 更新
  const updateMutation = useMutation({
    mutationFn: (data: BenefitFormData) => updateBenefit(benefitId!, data),
    onSuccess: () => {
      message.success('更新成功');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BENEFIT_LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BENEFIT_DETAIL, benefitId] });
      handleClose();
      onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('更新失败:', error);
      message.error(error.message || '更新失败');
    },
  });

  // 标记是否正在初始化数据，避免在数据回显时触发清空逻辑
  const isInitializingRef = React.useRef(false);

  // 监听 type 变化，在编辑模式下切换类型时处理 typeLimit
  useEffect(() => {
    // 如果正在初始化数据，不处理
    if (isInitializingRef.current) {
      return;
    }

    if (!open || !isEdit || !detailData || currentType === undefined) {
      return;
    }

    const originalType = detailData.type;

    // 如果切换到的类型和原始类型不同，清空 typeLimit
    if (currentType !== originalType) {
      form.setFieldValue('typeLimit', '');
    } else {
      // 如果切换回原始类型，恢复原来的 typeLimit 值
      const convertedValue = convertTypeLimitForForm(
        detailData.typeLimit,
        originalType
      );
      form.setFieldValue('typeLimit', convertedValue);
    }
  }, [currentType, detailData, isEdit, open, form]);

  // 加载详情数据到表单
  useEffect(() => {
    if (!open) {
      form.resetFields();
      isInitializingRef.current = false;
      return;
    }

    if (detailData && isEdit) {
      // 标记正在初始化数据
      isInitializingRef.current = true;
      // 根据 type 将 typeLimit 的值赋给对应的字段
      const formValues: any = {
        titleEn: detailData.titleEn || '',
        titleJa: detailData.titleJa || '',
        iconUrl: detailData.iconUrl || '',
        type: detailData.type,
        obtainContentEn: detailData.obtainContentEn || '',
        obtainContentJa: detailData.obtainContentJa || '',
        link: detailData.link || '',
        status: detailData.status ?? 1, // 默认启用
        showOrder: detailData.showOrder,
        groupKey: detailData.groupKey || '',
        groupCategory: detailData.groupCategory || undefined,
        corePrivilege: detailData.corePrivilege ?? 0,
      };

      // 根据不同的 type，将 typeLimit 的值转换为表单格式
      formValues.typeLimit = convertTypeLimitForForm(
        detailData.typeLimit,
        detailData.type
      );

      // 数据回显 将 API 返回的数据映射到表单字段
      form.setFieldsValue(formValues);
      
      // 数据回显完成后，取消初始化标记
      setTimeout(() => {
        isInitializingRef.current = false;
      }, 0);
    } else {
      form.resetFields();
      form.setFieldsValue(FORM_DEFAULT_VALUES);
      isInitializingRef.current = false;
    }
  }, [detailData, isEdit, open, form]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      
      // 根据 type 将 typeLimit 转换为提交格式
      values.typeLimit = convertTypeLimitForSubmit(values.typeLimit, values.type);
      
      const mutation = isEdit ? updateMutation : createMutation;
      mutation.mutate(values);
    } catch (error) {
      console.error('表单验证失败:', error);
      message.error('请检查表单填写是否正确');
    }
  }, [form, isEdit, updateMutation, createMutation]);

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const loading = detailLoading || createMutation.isPending || updateMutation.isPending;

  return (
    <Drawer
      title={isEdit ? '编辑会员权益' : '新增会员权益'}
      width={DRAWER_CONFIG.WIDTH}
      onClose={handleClose}
      open={open}
      placement={DRAWER_CONFIG.PLACEMENT}
      maskClosable={false}
      styles={{ body: { paddingBottom: 80 } }}
      extra={
        <Space>
          <Button onClick={handleClose}>取消</Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            {isEdit ? '保存' : '创建'}
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <BenefitForm form={form} />
      </Spin>
    </Drawer>
  );
};

export default BenefitDrawer;

